import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { getProgress } from 'src/api/courseRoutes';
import { paces, Progress } from 'src/interfaces/course';
import { AuthContext } from 'src/contexts/JWTAuthContext';
import { addVisitedSubmodule } from './courseProgressUtils';

type ICourseProgressContext = {
  progress: Progress | null;
  loading: boolean;
  refresh: () => Promise<void>;
  markVisited: (submoduleId: string) => void;
  updatePace: (pace: paces) => void;
};

export const CourseProgressContext = createContext<ICourseProgressContext>(
  {} as ICourseProgressContext
);

export const CourseProgressProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const { uid } = useContext(AuthContext);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [loading, setLoading] = useState(true);

  // loading only starts true for the initial fetch; refresh() intentionally
  // never sets it back to true so the sidebar menu doesn't flash on refetch
  // (e.g. after onboarding completes).
  const refresh = useCallback(async () => {
    if (!uid) return;
    try {
      const response = await getProgress(uid);
      if (response.success) {
        setProgress(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    if (uid) {
      refresh();
    } else {
      setLoading(false);
    }
  }, [uid, refresh]);

  const markVisited = useCallback((submoduleId: string) => {
    setProgress((prev) => addVisitedSubmodule(prev, submoduleId));
  }, []);

  // Optimistic update; callers persist via setProfile and refresh() on failure.
  const updatePace = useCallback((pace: paces) => {
    setProgress((prev) => (prev ? { ...prev, pace } : prev));
  }, []);

  return (
    <CourseProgressContext.Provider
      value={{ progress, loading, refresh, markVisited, updatePace }}
    >
      {children}
    </CourseProgressContext.Provider>
  );
};
